import { StandardEvents } from '@shopify/events';
import { Component } from '@theme/component';

class PaprLabNavigation extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.initialize();
  }
  updatedCallback() {
    super.updatedCallback();
    this.initialize();
  }
  initialize() {
    this.controller?.abort();
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.disclosures = [...this.querySelectorAll('[data-nav-disclosure]')];
    this.menuButton = this.querySelector('[data-mobile-toggle]');
    this.cartLink = this.querySelector('[data-cart-link]');
    this.classList.add('is-ready');
    this.menuButton?.setAttribute('aria-expanded', String(this.classList.contains('is-mobile-open')));
    this.scrollContainer = this.closest('.page-wrapper');
    this.onScroll = () => this.classList.toggle('is-scrolled', Math.max(window.scrollY, this.scrollContainer?.scrollTop ?? 0) > 12);
    this.onScroll();
    window.addEventListener('scroll', this.onScroll, { ...options, passive: true });
    this.scrollContainer?.addEventListener('scroll', this.onScroll, { ...options, passive: true });
    this.menuButton?.addEventListener('click', () => this.toggleMobile(), options);
    this.disclosures.forEach(details => {
      details.addEventListener('toggle', () => {
        if (details.open) this.disclosures.forEach(other => { if (other !== details) other.open = false; });
        this.syncOpenState();
      }, options);
    });
    document.addEventListener('pointerdown', event => { if (!this.contains(event.target)) this.close(); }, options);
    document.addEventListener('focusin', event => { if (!this.contains(event.target)) this.close(); }, options);
    this.addEventListener('keydown', event => {
      if (event.key !== 'Escape') return;
      const active = this.disclosures.find(details => details.open);
      if (active) {
        active.open = false;
        active.querySelector('summary')?.focus();
      } else if (this.classList.contains('is-mobile-open')) {
        this.toggleMobile(false);
        this.menuButton?.focus();
      }
      this.syncOpenState();
    }, options);
    this.addEventListener('click', event => {
      if (event.target.closest('a')) this.close();
    }, options);
    this.media = matchMedia('(min-width: 990px)');
    this.media.addEventListener('change', () => this.close(), options);
    document.addEventListener(StandardEvents.cartLinesUpdate, event => {
      if (event.promise) {
        event.promise.then(({ cart, detail }) => {
          const count = cart?.totalQuantity ?? detail?.itemCount;
          if (Number.isFinite(count)) this.updateCount(count);
          else this.refreshCart();
        }).catch(() => {});
      } else this.refreshCart();
    }, options);
    window.addEventListener('pageshow', event => { if (event.persisted) this.refreshCart(); }, options);
    window.addEventListener('focus', () => this.refreshCart(), options);
    if (this.cartLink?.hasAttribute('data-cart-drawer')) {
      const syncCartState = event => {
        if (event.target.id === 'cart-drawer') this.cartLink.setAttribute('aria-expanded', String(event.type === 'theme-drawer:open'));
      };
      document.addEventListener('theme-drawer:open', syncCartState, options);
      document.addEventListener('theme-drawer:close', syncCartState, options);
      customElements.whenDefined('theme-drawer').then(() => {
        if (!this.isConnected || !document.getElementById('cart-drawer')) return;
        this.cartLink.setAttribute('role', 'button');
        this.cartLink.setAttribute('aria-haspopup', 'dialog');
        this.cartLink.setAttribute('aria-controls', 'cart-drawer');
        this.cartLink.setAttribute('aria-expanded', String(document.getElementById('cart-drawer').hasAttribute('open')));
      });
      this.cartLink.addEventListener('keydown', event => {
        if (event.key === ' ' && this.cartLink.getAttribute('role') === 'button') {
          event.preventDefault();
          this.cartLink.click();
        }
      }, options);
      this.cartLink.addEventListener('click', event => {
        const drawer = document.getElementById('cart-drawer');
        if (!drawer || typeof drawer.open !== 'function') return;
        event.preventDefault();
        drawer.open();
      }, options);
    }
  }
  disconnectedCallback() {
    this.controller?.abort();
    super.disconnectedCallback();
  }
  syncOpenState() {
    this.classList.toggle('is-open', this.classList.contains('is-mobile-open') || this.disclosures.some(details => details.open));
  }
  toggleMobile(force) {
    const open = force ?? !this.classList.contains('is-mobile-open');
    this.classList.toggle('is-mobile-open', open);
    this.menuButton?.setAttribute('aria-expanded', String(open));
    this.querySelector('[data-menu-open]').hidden = open;
    this.querySelector('[data-menu-close]').hidden = !open;
    if (!open) this.disclosures.forEach(details => { details.open = false; });
    this.syncOpenState();
  }
  close() {
    this.disclosures.forEach(details => { details.open = false; });
    this.toggleMobile(false);
  }
  updateCount(count) {
    this.querySelectorAll('[data-cart-count]').forEach(node => { node.textContent = String(count); });
  }
  async refreshCart() {
    if (this.cartRefresh) return;
    this.cartRefresh = true;
    try {
      const response = await fetch(this.dataset.cartUrl, { headers: { Accept: 'application/json' }, signal: this.controller?.signal });
      if (!response.ok) return;
      const cart = await response.json();
      if (Number.isFinite(cart.item_count)) this.updateCount(cart.item_count);
    } catch {} finally { this.cartRefresh = false; }
  }
}
if (!customElements.get('paprlab-navigation')) customElements.define('paprlab-navigation', PaprLabNavigation);
